/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Recap
 * OpenAPI spec version: 1
 */
import * as zod from 'zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useApiFetch } from '../client.ts';
import type { ErrorType } from '../client.ts';
import { useMutationBehavior } from '../mutations.ts';
import { useQueryBehavior } from '../queries.ts';
import { PagedSourceListEntry, Source, SourceFromLinkResult } from './schemas.ts';
import type {
    ErrorResponse,
    ListSourcesParams,
    SourceFromLink,
    SourceUpdate,
    SourcesFromPick,
    SourcesUpdate,
    UploadSourceBody,
} from './schemas.ts';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const getListSourcesUrl = (params?: ListSourcesParams) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(key, value === null ? 'null' : String(value));
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0 ? `/workspace/sources?${stringifiedParams}` : `/workspace/sources`;
};

/**
 * @summary List Sources
 */
export const useListSourcesHook = (): ((
    params?: ListSourcesParams,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PagedSourceListEntry>) => {
    const customFetcher = useApiFetch();
    return (params?: ListSourcesParams, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListSourcesUrl(params), {
            ...options,
            method: 'GET',

            schema: PagedSourceListEntry,
        });
    };
};

export const getListSourcesQueryKey = (params?: ListSourcesParams) => {
    return ['workspace', 'sources', ...(params ? [params] : [])] as const;
};

export const useListSourcesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListSourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListSourcesQueryKey(params);

    const listSources = useListSourcesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>> = ({ signal }) =>
        listSources(params, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        { params },
        { url: `/workspace/sources`, operationId: 'list_sources', operationName: 'listSources' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSourcesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>>;
export type ListSourcesQueryError = ErrorType<ErrorResponse>;

export function useListSources<
    TData = Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params: undefined | ListSourcesParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSources<
    TData = Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListSourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSources<
    TData = Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListSourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Sources
 */

export function useListSources<
    TData = Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListSourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSourcesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListSourcesQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUploadSourceUrl = () => {
    return `/workspace/sources`;
};

/**
 * @summary Upload Source
 */
export const useUploadSourceHook = (): ((
    uploadSourceBody: UploadSourceBody,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Source>) => {
    const customFetcher = useApiFetch();
    return (uploadSourceBody: UploadSourceBody, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        const formData = new FormData();
        formData.append(`file`, uploadSourceBody.file);
        formData.append(`kind`, uploadSourceBody.kind);
        if (uploadSourceBody.client_id !== undefined && uploadSourceBody.client_id !== null) {
            formData.append(`client_id`, uploadSourceBody.client_id);
        }
        if (uploadSourceBody.project_id !== undefined && uploadSourceBody.project_id !== null) {
            formData.append(`project_id`, uploadSourceBody.project_id);
        }

        return customFetcher(getUploadSourceUrl(), {
            ...options,
            method: 'POST',
            body: formData,
            schema: Source,
        });
    };
};

export const getUploadSourceMutationKey = () => ['uploadSource'] as const;

export const useUploadSourceMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>,
        TError,
        UploadSourceMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>,
    TError,
    UploadSourceMutationVariables,
    TContext
> => {
    const mutationKey = getUploadSourceMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const uploadSource = useUploadSourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>,
        UploadSourceMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return uploadSource(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources` },
        { operationId: 'upload_source', operationName: 'uploadSource' }
    );

    return customOptions;
};

export type UploadSourceMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>>;
export type UploadSourceMutationBody = UploadSourceBody;
export type UploadSourceMutationError = ErrorType<ErrorResponse>;
export type UploadSourceMutationVariables = { data: UploadSourceBody };

/**
 * @summary Upload Source
 */
export const useUploadSource = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>,
            TError,
            UploadSourceMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadSourceHook>>>,
    TError,
    UploadSourceMutationVariables,
    TContext
> => {
    return useMutation(useUploadSourceMutationOptions(options), queryClient);
};
export const getUpdateSourcesUrl = () => {
    return `/workspace/sources`;
};

/**
 * @summary Update Sources
 */
export const useUpdateSourcesHook = (): ((
    sourcesUpdate: SourcesUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (sourcesUpdate: SourcesUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateSourcesUrl(), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(sourcesUpdate),
        });
    };
};

export const getUpdateSourcesMutationKey = () => ['updateSources'] as const;

export const useUpdateSourcesMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>,
        TError,
        UpdateSourcesMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>,
    TError,
    UpdateSourcesMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateSourcesMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateSources = useUpdateSourcesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>,
        UpdateSourcesMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return updateSources(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources` },
        { operationId: 'update_sources', operationName: 'updateSources' }
    );

    return customOptions;
};

export type UpdateSourcesMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>>;
export type UpdateSourcesMutationBody = SourcesUpdate;
export type UpdateSourcesMutationError = ErrorType<ErrorResponse>;
export type UpdateSourcesMutationVariables = { data: SourcesUpdate };

/**
 * @summary Update Sources
 */
export const useUpdateSources = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>,
            TError,
            UpdateSourcesMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourcesHook>>>,
    TError,
    UpdateSourcesMutationVariables,
    TContext
> => {
    return useMutation(useUpdateSourcesMutationOptions(options), queryClient);
};
export const getCreateSourceFromLinkUrl = () => {
    return `/workspace/sources/from-link`;
};

/**
 * @summary Create Source From Link
 */
export const useCreateSourceFromLinkHook = (): ((
    sourceFromLink: SourceFromLink,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SourceFromLinkResult>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (sourceFromLink: SourceFromLink, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateSourceFromLinkUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(sourceFromLink),
            schema: SourceFromLinkResult,
        });
    };
};

export const getCreateSourceFromLinkMutationKey = () => ['createSourceFromLink'] as const;

export const useCreateSourceFromLinkMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>,
        TError,
        CreateSourceFromLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>,
    TError,
    CreateSourceFromLinkMutationVariables,
    TContext
> => {
    const mutationKey = getCreateSourceFromLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createSourceFromLink = useCreateSourceFromLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>,
        CreateSourceFromLinkMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return createSourceFromLink(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/from-link` },
        { operationId: 'create_source_from_link', operationName: 'createSourceFromLink' }
    );

    return customOptions;
};

export type CreateSourceFromLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>
>;
export type CreateSourceFromLinkMutationBody = SourceFromLink;
export type CreateSourceFromLinkMutationError = ErrorType<ErrorResponse>;
export type CreateSourceFromLinkMutationVariables = { data: SourceFromLink };

/**
 * @summary Create Source From Link
 */
export const useCreateSourceFromLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>,
            TError,
            CreateSourceFromLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateSourceFromLinkHook>>>,
    TError,
    CreateSourceFromLinkMutationVariables,
    TContext
> => {
    return useMutation(useCreateSourceFromLinkMutationOptions(options), queryClient);
};
export const getCreateSourcesFromPickUrl = () => {
    return `/workspace/sources/from-pick`;
};

/**
 * @summary Create Sources From Pick
 */
export const useCreateSourcesFromPickHook = (): ((
    sourcesFromPick: SourcesFromPick,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SourceFromLinkResult[]>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (sourcesFromPick: SourcesFromPick, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateSourcesFromPickUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(sourcesFromPick),
            schema: zod.array(SourceFromLinkResult),
        });
    };
};

export const getCreateSourcesFromPickMutationKey = () => ['createSourcesFromPick'] as const;

export const useCreateSourcesFromPickMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>,
        TError,
        CreateSourcesFromPickMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>,
    TError,
    CreateSourcesFromPickMutationVariables,
    TContext
> => {
    const mutationKey = getCreateSourcesFromPickMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createSourcesFromPick = useCreateSourcesFromPickHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>,
        CreateSourcesFromPickMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return createSourcesFromPick(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/from-pick` },
        { operationId: 'create_sources_from_pick', operationName: 'createSourcesFromPick' }
    );

    return customOptions;
};

export type CreateSourcesFromPickMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>
>;
export type CreateSourcesFromPickMutationBody = SourcesFromPick;
export type CreateSourcesFromPickMutationError = ErrorType<ErrorResponse>;
export type CreateSourcesFromPickMutationVariables = { data: SourcesFromPick };

/**
 * @summary Create Sources From Pick
 */
export const useCreateSourcesFromPick = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>,
            TError,
            CreateSourcesFromPickMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateSourcesFromPickHook>>>,
    TError,
    CreateSourcesFromPickMutationVariables,
    TContext
> => {
    return useMutation(useCreateSourcesFromPickMutationOptions(options), queryClient);
};
export const getGetSourceUrl = (sourceId: string | null) => {
    return `/workspace/sources/${sourceId}`;
};

/**
 * @summary Get Source
 */
export const useGetSourceHook = (): ((
    sourceId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Source>) => {
    const customFetcher = useApiFetch();
    return (sourceId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetSourceUrl(sourceId), {
            ...options,
            method: 'GET',

            schema: Source,
        });
    };
};

export const getGetSourceQueryKey = (sourceId: string | null) => {
    return ['workspace', 'sources', sourceId] as const;
};

export const useGetSourceQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetSourceQueryKey(sourceId);

    const getSource = useGetSourceHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>> = ({ signal }) =>
        getSource(sourceId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: sourceId !== null && sourceId !== undefined, ...queryOptions },
        { sourceId },
        { url: `/workspace/sources/${sourceId}`, operationId: 'get_source', operationName: 'getSource' }
    );

    return customOptions as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type GetSourceQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>>;
export type GetSourceQueryError = ErrorType<ErrorResponse>;

export function useGetSource<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSource<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSource<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Source
 */

export function useGetSource<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSourceHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetSourceQueryOptions(sourceId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateSourceUrl = (sourceId: string) => {
    return `/workspace/sources/${sourceId}`;
};

/**
 * @summary Update Source
 */
export const useUpdateSourceHook = (): ((
    sourceId: string,
    sourceUpdate: SourceUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Source>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (sourceId: string, sourceUpdate: SourceUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateSourceUrl(sourceId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(sourceUpdate),
            schema: Source,
        });
    };
};

export const getUpdateSourceMutationKey = () => ['updateSource'] as const;

export const useUpdateSourceMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>,
        TError,
        UpdateSourceMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>,
    TError,
    UpdateSourceMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateSourceMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateSource = useUpdateSourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>,
        UpdateSourceMutationVariables
    > = (props) => {
        const { sourceId, data } = props ?? {};

        return updateSource(sourceId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/{sourceId}` },
        { operationId: 'update_source', operationName: 'updateSource' }
    );

    return customOptions;
};

export type UpdateSourceMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>>;
export type UpdateSourceMutationBody = SourceUpdate;
export type UpdateSourceMutationError = ErrorType<ErrorResponse>;
export type UpdateSourceMutationVariables = { sourceId: string; data: SourceUpdate };

/**
 * @summary Update Source
 */
export const useUpdateSource = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>,
            TError,
            UpdateSourceMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourceHook>>>,
    TError,
    UpdateSourceMutationVariables,
    TContext
> => {
    return useMutation(useUpdateSourceMutationOptions(options), queryClient);
};
export const getDeleteSourceUrl = (sourceId: string) => {
    return `/workspace/sources/${sourceId}`;
};

/**
 * @summary Delete Source
 */
export const useDeleteSourceHook = (): ((
    sourceId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (sourceId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteSourceUrl(sourceId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteSourceMutationKey = () => ['deleteSource'] as const;

export const useDeleteSourceMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>,
        TError,
        DeleteSourceMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>,
    TError,
    DeleteSourceMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteSourceMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteSource = useDeleteSourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>,
        DeleteSourceMutationVariables
    > = (props) => {
        const { sourceId } = props ?? {};

        return deleteSource(sourceId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/{sourceId}` },
        { operationId: 'delete_source', operationName: 'deleteSource' }
    );

    return customOptions;
};

export type DeleteSourceMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>>;

export type DeleteSourceMutationError = ErrorType<ErrorResponse>;
export type DeleteSourceMutationVariables = { sourceId: string };

/**
 * @summary Delete Source
 */
export const useDeleteSource = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>,
            TError,
            DeleteSourceMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteSourceHook>>>,
    TError,
    DeleteSourceMutationVariables,
    TContext
> => {
    return useMutation(useDeleteSourceMutationOptions(options), queryClient);
};
export const getProcessSourceUrl = (sourceId: string) => {
    return `/workspace/sources/${sourceId}/process`;
};

/**
 * @summary Process Source
 */
export const useProcessSourceHook = (): ((
    sourceId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Source>) => {
    const customFetcher = useApiFetch();
    return (sourceId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getProcessSourceUrl(sourceId), {
            ...options,
            method: 'POST',

            schema: Source,
        });
    };
};

export const getProcessSourceMutationKey = () => ['processSource'] as const;

export const useProcessSourceMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>,
        TError,
        ProcessSourceMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>,
    TError,
    ProcessSourceMutationVariables,
    TContext
> => {
    const mutationKey = getProcessSourceMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const processSource = useProcessSourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>,
        ProcessSourceMutationVariables
    > = (props) => {
        const { sourceId } = props ?? {};

        return processSource(sourceId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/{sourceId}/process` },
        { operationId: 'process_source', operationName: 'processSource' }
    );

    return customOptions;
};

export type ProcessSourceMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>>;

export type ProcessSourceMutationError = ErrorType<ErrorResponse>;
export type ProcessSourceMutationVariables = { sourceId: string };

/**
 * @summary Process Source
 */
export const useProcessSource = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>,
            TError,
            ProcessSourceMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useProcessSourceHook>>>,
    TError,
    ProcessSourceMutationVariables,
    TContext
> => {
    return useMutation(useProcessSourceMutationOptions(options), queryClient);
};
export const getTakeSourceUpdateUrl = (sourceId: string) => {
    return `/workspace/sources/${sourceId}/take-update`;
};

/**
 * @summary Take Source Update
 */
export const useTakeSourceUpdateHook = (): ((
    sourceId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Source>) => {
    const customFetcher = useApiFetch();
    return (sourceId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getTakeSourceUpdateUrl(sourceId), {
            ...options,
            method: 'POST',

            schema: Source,
        });
    };
};

export const getTakeSourceUpdateMutationKey = () => ['takeSourceUpdate'] as const;

export const useTakeSourceUpdateMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>,
        TError,
        TakeSourceUpdateMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>,
    TError,
    TakeSourceUpdateMutationVariables,
    TContext
> => {
    const mutationKey = getTakeSourceUpdateMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const takeSourceUpdate = useTakeSourceUpdateHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>,
        TakeSourceUpdateMutationVariables
    > = (props) => {
        const { sourceId } = props ?? {};

        return takeSourceUpdate(sourceId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/sources/{sourceId}/take-update` },
        { operationId: 'take_source_update', operationName: 'takeSourceUpdate' }
    );

    return customOptions;
};

export type TakeSourceUpdateMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>
>;

export type TakeSourceUpdateMutationError = ErrorType<ErrorResponse>;
export type TakeSourceUpdateMutationVariables = { sourceId: string };

/**
 * @summary Take Source Update
 */
export const useTakeSourceUpdate = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>,
            TError,
            TakeSourceUpdateMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useTakeSourceUpdateHook>>>,
    TError,
    TakeSourceUpdateMutationVariables,
    TContext
> => {
    return useMutation(useTakeSourceUpdateMutationOptions(options), queryClient);
};
export const getDownloadSourceFileUrl = (sourceFileId: string | null) => {
    return `/workspace/source-files/${sourceFileId}/file`;
};

/**
 * @summary Download Source File
 */
export const useDownloadSourceFileHook = (): ((
    sourceFileId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (sourceFileId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDownloadSourceFileUrl(sourceFileId), {
            ...options,
            method: 'GET',
        });
    };
};

export const getDownloadSourceFileQueryKey = (sourceFileId: string | null) => {
    return ['workspace', 'source-files', sourceFileId, 'file'] as const;
};

export const useDownloadSourceFileQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceFileId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getDownloadSourceFileQueryKey(sourceFileId);

    const downloadSourceFile = useDownloadSourceFileHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>> = ({ signal }) =>
        downloadSourceFile(sourceFileId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: sourceFileId !== null && sourceFileId !== undefined, ...queryOptions },
        { sourceFileId },
        {
            url: `/workspace/source-files/${sourceFileId}/file`,
            operationId: 'download_source_file',
            operationName: 'downloadSourceFile',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DownloadSourceFileQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>
>;
export type DownloadSourceFileQueryError = ErrorType<ErrorResponse>;

export function useDownloadSourceFile<
    TData = Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceFileId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadSourceFile<
    TData = Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceFileId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadSourceFile<
    TData = Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceFileId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Download Source File
 */

export function useDownloadSourceFile<
    TData = Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    sourceFileId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadSourceFileHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useDownloadSourceFileQueryOptions(sourceFileId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}
